import { api } from "@clarent/backend/convex/_generated/api";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { errorMessage } from "../lib/convexError";

/**
 * Feed rows from the design system: coloured dot plus a plain headline, never colour alone.
 * `headline` is the fallback for alerts raised before they stored their own (D37).
 */
const TYPE_META = {
  overdue: { headline: "is past its due date", dot: "bg-late" },
  at_risk: { headline: "is at risk", dot: "bg-risk" },
  start_due_soon: { headline: "needs to start soon", dot: "bg-tight" },
} as const;

const raisedDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "Europe/London" });
const raisedTime = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Europe/London" });
/** "18 Oct · 07:00" */
const raisedAt = { format: (ms: number) => `${raisedDay.format(ms)} · ${raisedTime.format(ms)}` };

export function AlertsPage() {
  const alerts = useQuery(api.alerts.listPending);
  const acknowledge = useMutation(api.alerts.acknowledge);
  const dismiss = useMutation(api.alerts.dismiss);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    try {
      setError(null);
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="page-title">Alerts</h1>
        <span className="text-[13px] text-muted">Checked every morning. Acknowledge to keep an alert, dismiss to clear it.</span>
      </div>
      {error && <p className="notice-error mb-3">{error}</p>}
      {alerts === undefined && <p className="text-sm text-muted">Loading alerts…</p>}
      {alerts && alerts.length === 0 && <div className="panel p-8 text-center text-sm text-muted">Nothing to look at. New alerts appear here after the morning check.</div>}
      {alerts && alerts.length > 0 && (
        <ul className="overflow-clip rounded-[14px] border border-line bg-bg">
          {alerts.map((alert) => {
            const meta = TYPE_META[alert.type];
            return (
              <li key={alert._id} className="flex flex-col gap-3 border-b border-line px-3.5 py-3 last:border-b-0 sm:flex-row sm:items-start">
                <span className={`mt-[5px] size-2 shrink-0 rounded-full ${meta.dot}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-[18px] font-bold">
                    <Link to="/jobs/$jobId" params={{ jobId: alert.jobId }} className="hover:text-brand-dark hover:underline">
                      {alert.headline ?? alert.jobReference}
                    </Link>
                    {alert.headline === undefined && ` ${meta.headline}`}
                  </p>
                  <p className="mt-0.5 text-[13px] leading-[1.45] text-muted">{alert.message}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                  <span className="font-mono text-[11px] leading-[14px] text-muted">{raisedAt.format(alert.createdAt)}</span>
                  <div className="flex gap-1">
                    <button className="btn-secondary" onClick={() => act(() => acknowledge({ alertId: alert._id }))}>
                      Acknowledge
                    </button>
                    <button className="btn-ghost" onClick={() => act(() => dismiss({ alertId: alert._id }))}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
